/**
 * Offset pagination for an administrative list.
 *
 * ## Why this exists
 *
 * Every list endpoint in this platform already took `limit` and `offset` and already returned
 * `total`, and every back-office page ignored all three: `fetchKbDocuments({})` returns the
 * server's default page of fifty and the table rendered exactly those, with nothing on screen
 * saying there were more. A knowledge base of four hundred documents looked like a knowledge base
 * of fifty — and the fifty shown were the newest, so the ones most likely to need attention were
 * the ones invisible.
 *
 * ## Decisions
 *
 * **Text labels, no arrow icons.** The icon set has no left/right chevron, and adding one would
 * mean mirroring it for Arabic — a directional arrow that does not flip in RTL points at the wrong
 * end of the list. Words carry the direction already, in both languages.
 *
 * **It renders nothing when everything fits.** A pager under a list of three documents is noise,
 * and a disabled pager reads as a broken one.
 *
 * **The range is announced.** `aria-live="polite"` on the summary, because the visual change on
 * pressing "next" is the table's contents, which a screen-reader user is not looking at. Without
 * it the only feedback is that focus stayed on a button.
 *
 * **Strings arrive as props**, as in `PasswordInput`: this package holds no translations, so the
 * `lint:i18n` check stays meaningful and each application words its own interface.
 */

import { Button } from "./Button";

export interface PagerProps {
  /** How many rows exist on the server, not how many are on screen. */
  total: number;
  /** Page size — the `limit` sent with the request. */
  limit: number;
  /** Index of the first row on screen. */
  offset: number;
  /** Called with the new offset. The page owns the fetch. */
  onChange: (offset: number) => void;
  /**
   * The range, already interpolated by the caller's own `t()` — e.g. "1–50 of 412".
   *
   * A string rather than three numbers because number formatting is a locale decision this
   * package cannot make: `i18next` renders digits per locale, and a component that formatted
   * them here would quietly disagree with every other number in the interface.
   */
  summary: string;
  previousLabel: string;
  nextLabel: string;
  /** Accessible name for the whole control, e.g. "Document list pages". */
  label: string;
  /** Disables both buttons while a fetch is in flight. */
  busy?: boolean;
}

export function Pager({
  total,
  limit,
  offset,
  onChange,
  summary,
  previousLabel,
  nextLabel,
  label,
  busy = false,
}: PagerProps) {
  if (total <= limit) return null;

  const onFirstPage = offset <= 0;
  const onLastPage = offset + limit >= total;

  return (
    <nav
      aria-label={label}
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3"
    >
      <p aria-live="polite" className="text-body-sm text-textSecondary">
        {summary}
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          // `Math.max` rather than trusting the arithmetic: a limit that changed while a later
          // page was open would otherwise send a negative offset, which the API rejects with a
          // 422 the operator reads as the list being broken.
          onClick={() => onChange(Math.max(0, offset - limit))}
          disabled={onFirstPage || busy}
        >
          {previousLabel}
        </Button>
        <Button
          size="sm"
          onClick={() => onChange(offset + limit)}
          disabled={onLastPage || busy}
        >
          {nextLabel}
        </Button>
      </div>
    </nav>
  );
}
